// frontend/src/utils/estimateTotals.ts

export interface EstimateTotalsResult {
  preDiscountSubtotal: number;
  /** Sum of line-level (labor + part) discounts. */
  totalDiscounts: number;
  /** Subtotal after line-level discounts (= sum of line total_price). */
  discountedSubtotal: number;
  /** Whole-job (header) discount applied on top of discountedSubtotal. */
  jobDiscountAmount: number;
  /** Discounted subtotal after the whole-job discount (pre-fees, pre-tax). */
  taxableSubtotal: number;
  /** Sum of all fees (taxable + non-taxable). 0 when fees omitted. */
  feesTotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
}

/** Fee amounts split by taxability — mirrors backend calculateDocumentTotal's fees arg. */
export interface FeesForTotals {
  taxable: number;
  nonTaxable: number;
}

/** Whole-job (header) discount — mirrors backend calculations.ts Discount. */
export interface WholeJobDiscountInput {
  type: 'percentage' | 'amount' | 'final_price';
  value: number;
}

interface LineItemForTotals {
  id?: number;
  total_price: number;
  labor_hours?: number | null;
  hourly_rate?: number | null;
  labor_discount_percentage?: number | null;
  labor_discount_amount?: number | null;
  /** 0/false = line excluded from the tax base (mig 094). Default: taxable. */
  is_taxable?: number | boolean | null;
  parts?: Array<{
    quantity: number;
    unit_price: number;
    discount_percentage?: number | null;
    discount_amount?: number | null;
  }>;
}

/**
 * Calculate estimate totals with correct pre-discount subtotal.
 *
 * Pre-discount subtotal = sum of (labor_hours * hourly_rate) + (parts qty * unit_price)
 * for each line item, before any discounts.
 *
 * Discounts = sum of all labor + part discount amounts.
 *
 * Discounted subtotal = pre-discount - discounts (cross-checked against sum of total_price).
 *
 * @param lineItems - all line items on the estimate
 * @param taxRate - tax rate as a percentage (e.g. 8.25)
 * @param lineItemStatus - optional map of lineItemId -> 'approved'|'declined'|'unselected'.
 *                         When provided with at least one 'approved', only approved items are included.
 * @param wholeJobDiscount - optional header discount applied to the whole job, AFTER
 *                           line-level discounts and BEFORE tax (mirrors backend
 *                           calculateDocumentTotal). Omit for no header discount.
 */
export function calculateEstimateTotals(
  lineItems: LineItemForTotals[],
  taxRate: number,
  lineItemStatus?: Record<number, string>,
  wholeJobDiscount?: WholeJobDiscountInput | null,
  fees?: FeesForTotals | null
): EstimateTotalsResult {
  const hasApproved = lineItemStatus
    ? Object.values(lineItemStatus).some(s => s === 'approved')
    : false;

  const includedItems = lineItems.filter(item => {
    if (!hasApproved || !lineItemStatus) return true;
    return lineItemStatus[item.id!] === 'approved';
  });

  let preDiscountSubtotal = 0;
  let totalDiscounts = 0;
  // Post-line-discount total of TAXABLE lines only (mirrors backend
  // recalcEstimateTotals' taxableSubtotal built from is_taxable-aware rows).
  let taxableLines = 0;

  for (const item of includedItems) {
    // Labor: raw = hours * rate
    const laborHours = item.labor_hours || 0;
    const hourlyRate = item.hourly_rate || 0;
    const rawLabor = laborHours * hourlyRate;

    // Labor discount
    let laborDiscount = 0;
    if (rawLabor > 0) {
      laborDiscount = item.labor_discount_amount
        || (rawLabor * (item.labor_discount_percentage || 0) / 100);
    }

    // Parts: raw = sum of qty * unit_price
    let rawParts = 0;
    let partsDiscount = 0;
    if (item.parts && item.parts.length > 0) {
      for (const part of item.parts) {
        const basePartCost = part.quantity * part.unit_price;
        rawParts += basePartCost;
        partsDiscount += part.discount_amount
          || (basePartCost * (part.discount_percentage || 0) / 100);
      }
    }

    preDiscountSubtotal += rawLabor + rawParts;
    totalDiscounts += laborDiscount + partsDiscount;

    // Default taxable; only an explicit 0/false excludes the line (mig 094).
    const lineIsTaxable = !(item.is_taxable === 0 || item.is_taxable === false);
    if (lineIsTaxable) {
      taxableLines += rawLabor - laborDiscount + rawParts - partsDiscount;
    }
  }

  preDiscountSubtotal = Math.round(preDiscountSubtotal * 100) / 100;
  totalDiscounts = Math.round(totalDiscounts * 100) / 100;
  taxableLines = Math.round(taxableLines * 100) / 100;
  const discountedSubtotal = Math.round((preDiscountSubtotal - totalDiscounts) * 100) / 100;

  // Whole-job (header) discount applies AFTER line-level discounts, BEFORE tax —
  // identical ordering to backend utils/calculations.ts calculateDocumentTotal.
  let jobDiscountAmount = 0;
  if (wholeJobDiscount && wholeJobDiscount.value != null) {
    switch (wholeJobDiscount.type) {
      case 'percentage':
        jobDiscountAmount = (discountedSubtotal * wholeJobDiscount.value) / 100;
        break;
      case 'amount':
        jobDiscountAmount = wholeJobDiscount.value;
        break;
      case 'final_price':
        jobDiscountAmount = discountedSubtotal - wholeJobDiscount.value;
        break;
    }
    // Never negative, never more than the subtotal it discounts.
    jobDiscountAmount = Math.max(0, Math.min(jobDiscountAmount, discountedSubtotal));
  }
  jobDiscountAmount = Math.round(jobDiscountAmount * 100) / 100;

  const taxableSubtotal = Math.round((discountedSubtotal - jobDiscountAmount) * 100) / 100;

  // Fees: taxable fees join the tax base; all fees add to the total —
  // mirrors backend calculateDocumentTotal exactly.
  const taxableFees = Math.round((fees?.taxable ?? 0) * 100) / 100;
  const nonTaxableFees = Math.round((fees?.nonTaxable ?? 0) * 100) / 100;
  const feesTotal = Math.round((taxableFees + nonTaxableFees) * 100) / 100;

  // The whole-job discount is spread proportionally across the taxable portion
  // of the lines (backend: discountedTaxableLines = taxableLines × taxableAmount/subtotal).
  const clampedTaxableLines = Math.max(0, Math.min(taxableLines, discountedSubtotal));
  const discountedTaxableLines = discountedSubtotal > 0
    ? Math.round((clampedTaxableLines * (taxableSubtotal / discountedSubtotal)) * 100) / 100
    : 0;

  const taxAmount = Math.round(((discountedTaxableLines + taxableFees) * taxRate / 100) * 100) / 100;
  const total = Math.round((taxableSubtotal + feesTotal + taxAmount) * 100) / 100;

  return {
    preDiscountSubtotal,
    totalDiscounts,
    discountedSubtotal,
    jobDiscountAmount,
    taxableSubtotal,
    feesTotal,
    taxRate,
    taxAmount,
    total,
  };
}

/**
 * The fees total to SHOW for a bill, given its fee rows and its stored totals.
 *
 * Prefers the per-fee `computed_amount` — the figure actually charged. Falls
 * back to `feesFromStored` when any row is missing one, which is the shape every
 * invoice snapshotted before `createFromWorkOrder` learned to carry the column
 * has: rows with a percentage and a cap but no amount. Summing those as zero
 * printed "$0.00" next to a total that included the fee, so the customer's bill
 * did not add up on a document nobody can go back and recompute.
 */
export function displayedFeesTotal(
  fees: Array<{ computed_amount?: number | null; calculation_method?: string; value?: number | null }> | null | undefined,
  doc: {
    subtotal?: number | null;
    discount_amount?: number | null;
    tax_amount?: number | null;
    total_amount?: number | null;
    diagnosis_credit_cents?: number | null;
  }
): number {
  const rows = fees ?? [];
  if (rows.length === 0) return 0;
  const allComputed = rows.every((f) => f.computed_amount != null);
  if (allComputed) {
    const sum = rows.reduce((s, f) => s + Number(f.computed_amount || 0), 0);
    return Math.round(sum * 100) / 100;
  }
  return feesFromStored(doc);
}

/**
 * Derive the fees total from a document's STORED money fields:
 * total = (subtotal − whole-job discount) + fees + tax  ⇒  fees = remainder.
 * Lets stored-field PDF breakdowns print a Fees row that always reconciles
 * without a network fetch. Sub-cent noise and negatives collapse to 0.
 */
export function feesFromStored(doc: {
  subtotal?: number | null;
  discount_amount?: number | null;
  tax_amount?: number | null;
  total_amount?: number | null;
  diagnosis_credit_cents?: number | null;
}): number {
  // The diagnosis credit is subtracted from the STORED total (post-tax), so it
  // has to be added back before the remainder can mean "fees". Without this, a
  // $175 credit reads as −$175 of fees, the clamp below floors it at zero, and
  // every real fee silently disappears from the customer's breakdown.
  const credit = Math.round(Number(doc.diagnosis_credit_cents) || 0) / 100;
  const remainder = (doc.total_amount || 0) + credit
    - ((doc.subtotal || 0) - (doc.discount_amount || 0))
    - (doc.tax_amount || 0);
  const rounded = Math.round(remainder * 100) / 100;
  return rounded >= 0.01 ? rounded : 0;
}
